import { useMemo } from "react";

import * as Handle from "../handle";

import type * as Repository from "../../../_repository";
import type * as Type from "../type";

type Props = {
	optionPmsGetAvailableOptionListQueryData: Repository.Query.QueryData.OptionPmsGetAvailableOptionList.OptionPmsGetAvailableOptionListQueryData;
	selectedOptionID?: string;
	selectedSubOptionID?: string;
};
type Result = {
	viewData: Type.ViewData;
};

export const useViewDataConverter = ({
	optionPmsGetAvailableOptionListQueryData: {
		optionPmsGetAvailableOptionList: { options },
	},
	selectedOptionID,
	selectedSubOptionID,
}: Props): Result => {
	const {
		onGetOption,
		onGetSubOptionList,
		onGetSubOption,
		onGetAvailableAmountOfOption,
		onGetAvailableAmountOfSubOption,
	} = Handle.useHandleOption({ options });

	/**
	 * サブオプション
	 */
	const subOptionList = useMemo(() => {
		const option = onGetOption(selectedOptionID);
		return onGetSubOptionList(option);
	}, [onGetOption, onGetSubOptionList, selectedOptionID]);

	/**
	 * オプション・サブオプションの在庫数
	 */
	const amountList = useMemo(() => {
		let availableAmount: number | undefined = 0;

		const option = onGetOption(selectedOptionID);
		if (!selectedSubOptionID) {
			const availableAmountOfOption = onGetAvailableAmountOfOption(option);

			availableAmount = availableAmountOfOption ?? undefined;
		}

		if (selectedSubOptionID) {
			const _subOptionList = onGetSubOptionList(option);
			const subOption = onGetSubOption(_subOptionList, selectedSubOptionID);
			const availableAmountOfSubOption = onGetAvailableAmountOfSubOption(subOption);
			availableAmount = availableAmountOfSubOption;
		}

		return availableAmount !== undefined ? [...Array(availableAmount)] : undefined;
	}, [
		onGetAvailableAmountOfOption,
		onGetAvailableAmountOfSubOption,
		onGetOption,
		onGetSubOption,
		onGetSubOptionList,
		selectedOptionID,
		selectedSubOptionID,
	]);

	return useMemo((): Result => {
		return {
			viewData: {
				optionList: options.map(({ optionID, name }) => ({
					id: optionID,
					value: optionID,
					label: name,
				})),
				subOptionList: subOptionList?.map(({ subOptionID, name }) => ({
					id: subOptionID,
					value: subOptionID,
					label: name,
				})),
				amountList: amountList?.map((_, index) => {
					const value = index + 1;
					return {
						id: value.toString(),
						value: value.toString(),
						label: value.toString(),
					};
				}),
			},
		};
	}, [amountList, options, subOptionList]);
};
